// src/pages/agents/EditAgent.jsx
import React from "react";
import dayjs from "dayjs";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AgentForm from "../../components/AgentForm";
import {
  useEditAgentMutation,
  useGetAgentByIdQuery,
} from "../../redux/api/agentsApi";
import { useUploadImageMutation } from "../../redux/api/filesApi";
import { errorToast } from "../../constants/toast";

// helpers
const isFileLike = (v) =>
  typeof v === "object" &&
  v !== null &&
  (v instanceof File || (typeof Blob !== "undefined" && v instanceof Blob));

const makeFormFileName = (fileOrBlob, fallbackBase) => {
  if (fileOrBlob instanceof File) return fileOrBlob.name;
  return `${fallbackBase}-${Date.now()}.bin`;
};

export default function EditAgent() {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const { data: agent, isFetching, isSuccess } = useGetAgentByIdQuery(agentId, {
    refetchOnMountOrArgChange: true,
  });

  const [editAgent, { isLoading }] = useEditAgentMutation();
  const [uploadImage] = useUploadImageMutation();

  const uploadImg = async (fileOrBlob, imageType, tag) => {
    const fd = new FormData();
    fd.append("", fileOrBlob, makeFormFileName(fileOrBlob, imageType));
    const url = await uploadImage({ image: fd, imageType, tag }).unwrap();
    return url; // backend returns string filename/URL
  };

  const onEditAgent = async (data) => {
    try {
      const {
        agentLanguages,
        agentimage,
        agentimageFile, // new cropped blob
        nicimg,         // can be File or existing string
        passportFile,   // File (image)
        contractFile,   // File (pdf)
        idIssueDate,
        idExpiryDate,
        passportIssueDate,
        passportExpiryDate,
        ...rest
      } = data;

      const finalPayload = {
        ...rest,
        languageList: Array.isArray(agentLanguages)
          ? agentLanguages.map((l) => l.id)
          : [],
      };

      // --- profile image -> agentimage ---
      if (isFileLike(agentimageFile)) {
        finalPayload.agentimage = await uploadImg(agentimageFile, "agentimg", "img");
      } else if (typeof agentimage === "string" && agentimage.trim()) {
        finalPayload.agentimage = agentimage.trim();
      } else {
        finalPayload.agentimage = "";
      }

      // --- NIC/ID image -> nicimg ---
      if (isFileLike(nicimg)) {
        finalPayload.nicimg = await uploadImg(nicimg, "agentid", "img");
      } else if (typeof nicimg === "string" && nicimg.trim()) {
        finalPayload.nicimg = nicimg.trim();
      } else {
        finalPayload.nicimg = "";
      }

      // --- passport image -> passportimg ---
      if (isFileLike(passportFile)) {
        finalPayload.passportimg = await uploadImg(passportFile, "agentpass", "img");
      } else if (typeof data.passportimg === "string" && data.passportimg.trim()) {
        finalPayload.passportimg = data.passportimg.trim();
      } else {
        finalPayload.passportimg = "";
      }

      // --- contract pdf -> contractpdf ---
      if (isFileLike(contractFile)) {
        finalPayload.contractpdf = await uploadImg(contractFile, "contract", "pdf");
      } else if (typeof data.contractpdf === "string" && data.contractpdf.trim()) {
        finalPayload.contractpdf = data.contractpdf.trim();
      } else {
        finalPayload.contractpdf = "";
      }

      // --- dates mapping (ISO or null) ---
      finalPayload.idissuedate = idIssueDate?.toISOString?.() ?? null;
      finalPayload.idexpdate = idExpiryDate?.toISOString?.() ?? null;
      finalPayload.passportissuedate = passportIssueDate?.toISOString?.() ?? null;
      finalPayload.passportexpdate = passportExpiryDate?.toISOString?.() ?? null;

      await editAgent(finalPayload).unwrap();
      navigate("/agents");
    } catch (e) {
      errorToast("Unknown Error Occurred");
    }
  };

  if (isFetching) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "grid",
          placeItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (isSuccess && agent && agent.length > 0) {
    const a = agent[0];

    // Prefill form: convert API -> RHF fields
    const agentData = {
      ...a,

      // keep existing URL strings so previews show up
      agentimage: a.agentimage || "",
      nicimg: a.nicimg || "",
      passportimg: a.passportimg || "",
      contractpdf: a.contractpdf || "",

      // convert API date fields -> picker fields
      idIssueDate: a.idissuedate ? dayjs(a.idissuedate) : null,
      idExpiryDate: a.idexpdate ? dayjs(a.idexpdate) : null,
      passportIssueDate: a.passportissuedate ? dayjs(a.passportissuedate) : null,
      passportExpiryDate: a.passportexpdate ? dayjs(a.passportexpdate) : null,

      // RHF file placeholders (no file selected initially)
      agentimageFile: null,
      passportFile: null,
      contractFile: null,

      // languages to RHF shape
      agentLanguages: Array.isArray(a.laguages)
        ? a.laguages.map((id) => ({ id }))
        : [],
    };

    return (
      <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
        <AgentForm
          agentData={agentData}
          onEditAgent={onEditAgent}
          isSubmitting={isLoading}
        />
      </Box>
    );
  }

  return <div>Staff not found</div>;
}
